import { apiCall } from 'src/utils/ApiCall';
import {
  GetControlSummaryDataOutput,
  RecordAttributes,
} from '@amzn/controllershiphub-typescript-client';
import { filterAndCleanRecords } from 'src/components/pages/SummaryPage';
import { CONTROL_APIS, ApiError } from 'src/common/config/ApiConstants';
import { ControlId } from 'src/constants/api';
import { LEASE_TYPES, LEASE_TYPE_FIELD } from 'src/constants/grc5253';

/**
 * Gets the leaseType from URL query parameters
 * @returns 'AWS' | 'SDO' | null based on URL - returns null if not explicitly specified
 */
export const getLeaseTypeFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const leaseType = urlParams.get(LEASE_TYPE_FIELD);
  if (leaseType === LEASE_TYPES.SDO) {
    return LEASE_TYPES.SDO;
  } else if (leaseType === LEASE_TYPES.AWS) {
    return LEASE_TYPES.AWS;
  }
  // Return null if leaseType is not explicitly specified in URL
  // This allows backward compatibility - no filter applied by default
  return null;
};

interface DataResponse {
  records: RecordAttributes[][];
  totalRecords: number;
  distinctValues?: Record<string, string[]>;
}

export const getControlData = async (
  controlId: string,
  filters: Record<string, string[]> = {}
): Promise<DataResponse> => {
  try {
    // For GRC5253, add leaseType filter based on URL parameter in all environments.
    // In prod, only AWS button navigates internally (STORES goes to external link),
    // so only ?leaseType=AWS will be present. In beta/gamma, both buttons navigate internally.
    const enhancedFilters = { ...filters };

    if (controlId === ControlId.GRC5253 && !enhancedFilters[LEASE_TYPE_FIELD]) {
      const leaseTypeFromUrl = getLeaseTypeFromUrl();
      // Only add filter if leaseType is explicitly specified in URL
      if (leaseTypeFromUrl) {
        enhancedFilters[LEASE_TYPE_FIELD] = [leaseTypeFromUrl];
      }
    }

    const queryParams = {
      limit: CONTROL_APIS.BATCH_SIZE.toString(),
      ...(Object.keys(enhancedFilters).length > 0 && {
        filters: JSON.stringify(enhancedFilters),
      }),
    };

    let allRecords: RecordAttributes[][] = [];
    let lastKey: string | undefined;
    let distinctValues: Record<string, string[]> | undefined;
    let fetchedRecords = 0;

    do {
      const response = await apiCall<GetControlSummaryDataOutput>({
        endpoint: CONTROL_APIS.ENDPOINTS.DATA(controlId),
        queryParams: lastKey ? { ...queryParams, exclusiveStartKey: lastKey } : queryParams,
      });

      if (response.controlData?.records) {
        const newRecordsCount = response.controlData.records.length;
        allRecords = allRecords.concat(response.controlData.records);
        fetchedRecords += newRecordsCount;
      }

      if (response.distinctValues && !lastKey) {
        distinctValues = response.distinctValues;
      }

      lastKey = response.lastEvaluatedKey;
    } while (lastKey);

    const cleanedRecords = filterAndCleanRecords(allRecords);

    return {
      records: cleanedRecords,
      totalRecords: cleanedRecords.length,
      distinctValues,
    };
  } catch (err: unknown) {
    const error = err as Error;
    if (error instanceof ApiError && error.status === 401) {
      throw new Error(CONTROL_APIS.ERROR_MESSAGES.UNAUTHORIZED);
    }
    throw new Error(CONTROL_APIS.ERROR_MESSAGES.DATA_FETCH_FAILED);
  }
};
